import {
  Code2,
  MonitorSmartphone,
  ShieldCheck,
  Cloud,
  BrainCircuit,
  Server,
  Compass,
  Headset,
  Users,
  Zap,
  Wrench,
  Sun,
  Building,
  Landmark,
  HeartPulse,
  GraduationCap,
  Building2,
  Factory,
  ShoppingCart,
  Rocket,
  Briefcase,
  Lightbulb,
  Handshake,
  Award,
  Target,
  Shield,
  TrendingUp,
  Sparkles,
  Layers,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", hasMenu: true },
  { label: "Industries", to: "/industries" },
  { label: "Why EVOVLEX", to: "/why-evovlex" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "software-development",
    icon: Code2,
    title: "Software Development",
    short: "Custom software solutions, enterprise applications, APIs and business automation platforms.",
    headline: "Build. Innovate. Scale.",
    intro: "We develop customized software solutions that solve real business problems.",
    outro: "From concept to deployment, our focus remains on performance, scalability, security and user experience.",
    capabilities: [
      "Enterprise Software",
      "Web Applications",
      "Mobile Applications",
      "API Development",
      "SaaS Platforms",
      "Business Automation",
      "CRM & Business Solutions",
      "Custom Applications",
      "System Integration",
    ],
    positioning: ["Performance", "Scalability", "Security", "User Experience"],
    meta: "Custom software, enterprise applications, APIs, SaaS platforms and business automation engineered for performance, scalability and security.",
  },
  {
    slug: "web-mobile-development",
    icon: MonitorSmartphone,
    title: "Web & Mobile Development",
    short: "Modern, responsive and high-performance websites and mobile applications.",
    headline: "Modern. Responsive. High-Performance.",
    intro: "EVOVLEX IT builds modern, responsive and high-performance websites and mobile applications designed around your business requirements.",
    capabilities: [
      "Modern Websites",
      "Responsive Experiences",
      "High-Performance Builds",
      "Mobile Applications",
    ],
    positioning: ["Modern", "Responsive", "High-Performance"],
    meta: "Modern, responsive and high-performance websites and mobile applications built by EVOVLEX IT.",
  },
  {
    slug: "cybersecurity",
    icon: ShieldCheck,
    title: "Cybersecurity",
    short: "Comprehensive security solutions to identify vulnerabilities, protect critical systems and respond to cyber threats.",
    headline: "Security That Keeps You Ahead",
    intro: "Cyber threats are evolving every day. EVOVLEX IT helps organizations strengthen their security posture with proactive, intelligence-driven cybersecurity solutions.",
    outro: "We help businesses build resilient security frameworks capable of addressing today's threats while preparing for tomorrow's challenges.",
    capabilities: [
      "Security Assessment",
      "Vulnerability Assessment & Penetration Testing (VAPT)",
      "SOC Services",
      "Network Security",
      "Endpoint Security",
      "Application Security",
      "Incident Response",
      "Digital Forensics",
      "Cyber Risk Assessment",
      "Security Monitoring",
      "Security Audits",
    ],
    philosophy: {
      label: "Our Philosophy",
      steps: ["Identify", "Protect", "Detect", "Respond", "Recover"],
    },
    meta: "Proactive, intelligence-driven cybersecurity services: VAPT, SOC, network, endpoint and application security, incident response and digital forensics.",
  },
  {
    slug: "cloud-solutions",
    icon: Cloud,
    title: "Cloud Solutions",
    short: "Cloud migration, infrastructure management, optimization, backup and disaster recovery.",
    headline: "Cloud Without Complexity",
    intro: "Modernize your infrastructure with secure and scalable cloud technologies.",
    outro: "We help organizations move towards flexible, resilient and cost-effective IT environments.",
    capabilities: [
      "Cloud Migration",
      "Cloud Infrastructure",
      "Cloud Security",
      "Cloud Management",
      "Backup & Disaster Recovery",
      "Infrastructure Optimization",
      "Hybrid Cloud Solutions",
      "Cloud Monitoring",
    ],
    meta: "Cloud migration, infrastructure, security, management, backup and disaster recovery — secure and scalable cloud solutions by EVOVLEX IT.",
  },
  {
    slug: "ai-machine-learning",
    icon: BrainCircuit,
    title: "AI & Machine Learning",
    short: "AI-powered automation, intelligent applications, predictive analytics and machine learning solutions.",
    headline: "Intelligence for the Next Generation of Business",
    intro: "Artificial intelligence is transforming how organizations operate. EVOVLEX IT helps businesses leverage AI and machine learning to automate processes, analyze information and make better decisions.",
    capabilities: [
      "AI-powered Applications",
      "Machine Learning",
      "Predictive Analytics",
      "Intelligent Automation",
      "Data Analytics",
      "Business Intelligence",
      "Natural Language Processing",
      "AI Consulting",
    ],
    positioningStatement: "Turn data into intelligence. Intelligence into action.",
    meta: "AI-powered applications, machine learning, predictive analytics, intelligent automation and AI consulting by EVOVLEX IT.",
  },
  {
    slug: "it-infrastructure",
    icon: Server,
    title: "IT Infrastructure",
    short: "Servers, networks, systems integration, infrastructure management and technical support.",
    headline: "Reliable Foundations for Digital Operations",
    intro: "EVOVLEX IT delivers reliable infrastructure, networking, servers and technology management that keep modern businesses running.",
    capabilities: [
      "Servers",
      "Networks",
      "Systems Integration",
      "Infrastructure Management",
      "Technical Support",
    ],
    meta: "Servers, networks, systems integration, infrastructure management and technical support by EVOVLEX IT.",
  },
  {
    slug: "it-consulting",
    icon: Compass,
    title: "IT Consulting",
    short: "Technology strategy, digital transformation, architecture and implementation consulting.",
    headline: "Technology Strategy. Business Results.",
    intro: "Technology investments should create measurable business value. Our IT consulting services help organizations identify technology opportunities, overcome challenges and develop practical strategies for digital growth.",
    capabilities: [
      "IT Strategy",
      "Digital Transformation",
      "Technology Assessment",
      "Cybersecurity Consulting",
      "Cloud Consulting",
      "Infrastructure Consulting",
      "IT Architecture",
      "Technology Roadmaps",
    ],
    meta: "IT strategy, digital transformation, technology assessment, architecture and technology roadmaps — consulting by EVOVLEX IT.",
  },
  {
    slug: "managed-it-services",
    icon: Headset,
    title: "Managed IT Services",
    short: "Continuous monitoring, technical support, infrastructure management and proactive IT operations.",
    headline: "Your Technology. Our Responsibility.",
    intro: "EVOVLEX IT provides proactive IT management designed to keep your technology environment secure, stable and operational.",
    capabilities: [
      "IT Helpdesk",
      "Remote Technical Support",
      "Network Monitoring",
      "Server Monitoring",
      "Security Monitoring",
      "Infrastructure Management",
      "Backup Management",
      "System Maintenance",
    ],
    meta: "Proactive managed IT services: helpdesk, remote support, network, server and security monitoring, backup and maintenance.",
  },
];

export const OM_SERVICE = {
  slug: "om-manpower-services",
  icon: Users,
  title: "O&M Manpower Services",
  tagline: "Reliable Manpower. Efficient Operations. Better Performance.",
  short: "Professional Operation & Maintenance manpower services to support industrial, infrastructure, renewable energy, electrical, mechanical, facility management and other operational requirements.",
  intro: "EVOVLEX IT provides professional Operation & Maintenance (O&M) manpower services to support industrial, infrastructure, renewable energy, electrical, mechanical, facility management, and other operational requirements.",
  intro2: "We understand that efficient operations depend on having the right people, with the right skills, at the right location. Our objective is to provide reliable and trained manpower that helps clients maintain smooth operations, improve productivity, minimize downtime, and maintain workplace safety.",
  categories: [
    {
      icon: Users,
      title: "Operations Manpower",
      roles: ["Plant / Site Operators", "Shift Operators", "Field Operators", "Control Room Operators", "Site Supervisors", "Operation Executives", "Technicians", "Helpers and Support Staff"],
    },
    {
      icon: Zap,
      title: "Electrical Maintenance Manpower",
      roles: ["Electrical Supervisors", "Electrical Technicians", "Electricians", "Maintenance Assistants", "HT/LT Electrical Support Staff", "Electrical Helpers"],
    },
    {
      icon: Wrench,
      title: "Mechanical Maintenance Manpower",
      roles: ["Mechanical Supervisors", "Mechanical Technicians", "Fitters", "Maintenance Technicians", "Welders", "Helpers", "Mechanical Maintenance Support Staff"],
    },
    {
      icon: Sun,
      title: "Solar / Renewable Energy O&M Manpower",
      note: "We can provide manpower for renewable energy and solar projects, including:",
      roles: ["Solar Plant Operators", "Solar Technicians", "Electrical Technicians", "Module Cleaning Teams", "Site Supervisors", "Preventive Maintenance Teams", "Breakdown Maintenance Teams", "Vegetation / Site Maintenance Workers", "General Helpers"],
    },
    {
      icon: Building,
      title: "Facility & Site Support Manpower",
      roles: ["Facility Supervisors", "Housekeeping Staff", "General Maintenance Staff", "Site Helpers", "Store Assistants", "Material Handling Staff", "Administrative Support Staff"],
    },
  ],
  approach: ["Plan", "Deploy", "Operate", "Maintain", "Monitor", "Improve"],
  approachIntro: "We work closely with clients to understand their manpower requirements, site conditions, shift patterns, skill requirements and operational objectives.",
  keyActivities: [
    "Manpower requirement assessment",
    "Recruitment and screening",
    "Skill-based manpower deployment",
    "Site supervision",
    "Shift management",
    "Attendance and manpower monitoring",
    "Preventive maintenance support",
    "Breakdown maintenance support",
    "Daily operational assistance",
    "Safety compliance support",
    "Replacement manpower arrangement",
    "Performance monitoring",
    "Monthly manpower documentation",
  ],
  manpowerCategories: [
    { category: "Management", roles: "O&M Manager, Project Manager" },
    { category: "Supervision", roles: "Site Supervisor, Shift Supervisor" },
    { category: "Technical", roles: "Electrical Technician, Mechanical Technician" },
    { category: "Skilled", roles: "Electrician, Fitter, Welder, Operator" },
    { category: "Semi-Skilled", roles: "Maintenance Assistant, Operator Assistant" },
    { category: "Unskilled", roles: "Helpers, General Workers, Site Support Staff" },
  ],
  industries: [
    "Renewable Energy & Solar Power",
    "Electrical Projects",
    "Manufacturing Industries",
    "Infrastructure Projects",
    "Industrial Plants",
    "Telecom Infrastructure",
    "Facility Management",
    "Warehousing & Logistics",
    "Construction & Project Sites",
    "Commercial & Institutional Facilities",
  ],
  benefits: [
    "Reduced manpower management burden",
    "Flexible workforce deployment",
    "Better workforce availability",
    "Skilled technical support",
    "Improved operational continuity",
    "Effective site supervision",
    "Support for preventive maintenance",
    "Reduced operational downtime",
    "Streamlined attendance and manpower administration",
  ],
  flexibility: [
    "Day Shift",
    "Night Shift",
    "General Shift",
    "Rotational Shift",
    "24×7 Operations",
    "Short-Term Requirements",
    "Long-Term Contracts",
    "Project-Based Deployment",
  ],
  whyChoose: [
    { title: "Skilled & Suitable Manpower", desc: "We focus on deploying manpower according to the specific skill and experience requirements of each project." },
    { title: "Reliable Deployment", desc: "Our objective is to maintain continuous manpower availability and minimize operational disruptions." },
    { title: "Site Supervision", desc: "Dedicated supervisors can be deployed to coordinate manpower, work allocation, attendance and site requirements." },
    { title: "Safety-Focused Approach", desc: "We promote safe working practices and compliance with applicable site safety procedures and client requirements." },
  ],
  commitment: "We are committed to providing dependable manpower, professional supervision and operational support that aligns with our clients' project requirements. Our focus is not simply on supplying manpower, but on building a reliable workforce ecosystem that supports productivity, safety, quality and operational continuity.",
  meta: "Professional Operation & Maintenance manpower services for industrial, infrastructure, renewable energy, electrical, mechanical and facility management operations.",
};

export const APPROACH_STEPS = [
  { label: "Understand", desc: "We begin by understanding your business challenges." },
  { label: "Strategize", desc: "We develop the right technology strategy." },
  { label: "Build", desc: "We implement the solution." },
  { label: "Secure", desc: "Security is embedded throughout." },
  { label: "Scale", desc: "We continuously optimize it for future growth." },
];

export const INDUSTRIES = [
  { icon: Landmark, title: "Banking & Financial Services", desc: "Secure and reliable technology for financial operations." },
  { icon: HeartPulse, title: "Healthcare", desc: "Technology solutions focused on security, reliability and operational efficiency." },
  { icon: GraduationCap, title: "Education", desc: "Digital platforms and technology solutions for modern education." },
  { icon: Building2, title: "Government & Public Sector", desc: "Secure and scalable digital infrastructure and technology solutions." },
  { icon: Factory, title: "Manufacturing", desc: "Technology-driven automation and operational transformation." },
  { icon: ShoppingCart, title: "Retail & E-Commerce", desc: "Digital platforms and intelligent solutions for connected businesses." },
  { icon: Rocket, title: "Startups & Technology Companies", desc: "Scalable technology foundations designed for rapid growth." },
  { icon: Briefcase, title: "Professional Services", desc: "Technology solutions that improve productivity and business operations." },
];

export const WHY_EVovLEX = [
  { icon: Target, title: "Business-Focused Technology", desc: "We focus on solving business problems—not simply implementing technology." },
  { icon: Shield, title: "Security-First Approach", desc: "Security is considered throughout the technology lifecycle." },
  { icon: TrendingUp, title: "Scalable Solutions", desc: "Our solutions are designed to grow alongside your business." },
  { icon: Sparkles, title: "Innovation Driven", desc: "We continuously explore emerging technologies to create better solutions." },
  { icon: Layers, title: "End-to-End Expertise", desc: "From strategy and development to security and support, we provide comprehensive technology capabilities." },
  { icon: Handshake, title: "Long-Term Partnership", desc: "We aim to build lasting relationships and continuously deliver value." },
];

export const VALUES = [
  { icon: Lightbulb, title: "Innovation", desc: "We embrace new ideas and emerging technologies." },
  { icon: Handshake, title: "Integrity", desc: "We believe in transparency, accountability and ethical business practices." },
  { icon: Shield, title: "Security", desc: "We prioritize the protection of systems, data and digital assets." },
  { icon: Award, title: "Excellence", desc: "We strive for high standards in everything we deliver." },
  { icon: Target, title: "Customer Success", desc: "Our success is measured by the value we create for our clients." },
];

export const MISSION = "To empower businesses with secure, innovative and scalable technology solutions that improve efficiency, strengthen digital resilience and accelerate sustainable growth.";

export const VISION = "To become a trusted global technology partner, recognized for excellence in IT services, cybersecurity, cloud computing, artificial intelligence and digital transformation.";

export const CONTACT_PLACEHOLDERS = {
  email: "[Your Official Email]",
  phone: "+91 7706355112",
  address: "Office No. 321, 3rd Floor, Siddhi Nisarga Plaza, Bhumkar Wasti, Hinjewadi Road, Pune.",
};

export const MARQUEE_ITEMS = [
  "Software Development",
  "Cybersecurity",
  "Cloud Solutions",
  "AI & Machine Learning",
  "IT Infrastructure",
  "IT Consulting",
  "Managed IT Services",
  "O&M Manpower",
  "Digital Transformation",
];

export const IMAGES = {
  hero: "/images/hero-network.png",
  techForward: "/images/tech-forward.png",
  omSolar: "/images/om-solar.png",
  about: "/images/about-abstract.png",
};
